import { useParams } from 'react-router-dom';
import { useBlogPost } from '../../hooks/useBlogPost';
import { Helmet } from 'react-helmet';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { NotFound } from '../NotFound';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

export function BlogPost() {
  const { slug } = useParams();
  const { post, isLoading, error } = useBlogPost(slug);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-center py-16">Error loading blog post</div>;
  if (!post) return <NotFound />;

  const renderOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p className="text-gray-600 leading-relaxed mb-6">{children}</p>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{children}</h3>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="list-disc list-outside ml-6 mb-8 space-y-2">{children}</ul>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className="text-gray-600 leading-relaxed">{children}</li>
      ),
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title} - Furgos Río</title>
        <meta name="description" content={post.seoDescription} />
      </Helmet>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Breadcrumbs
          items={[
            { name: 'Blog', href: '/blog' },
            { name: post.title, href: `/blog/${post.slug}` },
          ]}
        />

        <header className="mb-8">
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            <span className="mx-2">·</span>
            <span>{post.author}</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
          <div className="flex flex-wrap gap-2 mb-6">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-brand/10 text-brand"
              >
                {tag}
              </span>
            ))}
          </div>
        </header>

        <div className="mb-8">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-[400px] object-cover rounded-2xl"
          />
        </div>

        <div className="prose prose-lg max-w-none
          prose-headings:text-gray-900 prose-headings:font-bold
          prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
          prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
          prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-6
          prose-ul:mt-4 prose-ul:mb-8 prose-ul:list-disc prose-ul:list-outside
          prose-li:text-gray-600 prose-li:mb-2 prose-li:leading-relaxed"
        >
          {documentToReactComponents(post.content, renderOptions)}
        </div>

        {/* Related Posts Section */}
        {post.relatedPosts && post.relatedPosts.length > 0 && (
          <div className="mt-16 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Artículos relacionados</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {post.relatedPosts.map(relatedSlug => (
                <Link 
                  key={relatedSlug}
                  to={`/blog/${relatedSlug}`}
                  className="group"
                >
                  <div className="relative h-48 mb-4 overflow-hidden rounded-lg">
                    <img
                      src={post.relatedPostsData?.[relatedSlug]?.coverImage}
                      alt={post.relatedPostsData?.[relatedSlug]?.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-brand mb-2">
                    {post.relatedPostsData?.[relatedSlug]?.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-2">
                    {post.relatedPostsData?.[relatedSlug]?.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
} 